import Phaser from 'phaser';
import { t } from '../../i18n';

export type DamageNumberType = 'normal' | 'crit' | 'player';

const DAMAGE_COLOR: Record<DamageNumberType, string> = {
  normal: '#ffffff',
  crit:   '#ffee00',
  player: '#ff3333',
};

const DAMAGE_FONT_SIZE: Record<DamageNumberType, string> = {
  normal: '16px',
  crit:   '22px',
  player: '18px',
};

/**
 * VFXSystem — central hub for visual effects and "game juice".
 *
 * Instantiate once in the Game scene and store in the scene registry so that
 * entities can retrieve it with `scene.registry.get('vfx')`.
 */
export class VFXSystem {
  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Screen Shake ─────────────────────────────────────────────────────────

  /** Shake the main camera. intensity in px, duration in ms. */
  screenShake(intensityPx = 6, durationMs = 180): void {
    this.scene.cameras.main.shake(durationMs, intensityPx / 1000);
  }

  // ── Damage Numbers ────────────────────────────────────────────────────────

  /**
   * Display a floating damage number at world position (x, y).
   * 'normal' → white, 'crit' → yellow + "!", 'player' → red.
   */
  showDamageNumber(x: number, y: number, amount: number, type: DamageNumberType = 'normal'): void {
    const label = type === 'crit' ? `${Math.round(amount)}!` : `${Math.round(amount)}`;
    const text = this.scene.add
      .text(x, y, label, {
        fontSize: DAMAGE_FONT_SIZE[type],
        fontFamily: 'Arial Black, Arial',
        color: DAMAGE_COLOR[type],
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1)
      .setDepth(300);

    this.scene.tweens.add({
      targets: text,
      y: y - 55,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  // ── Death Particles ───────────────────────────────────────────────────────

  /**
   * Burst of small colored circles radiating from (x, y).
   * big=true uses more, larger, faster particles (for boss deaths).
   */
  enemyDeathParticles(x: number, y: number, color: number, big = false): void {
    const count = big ? 24 : 12;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const speed = big ? 80 + Math.random() * 180 : 40 + Math.random() * 90;
      const r     = big ? 4 + Math.random() * 5    : 2 + Math.random() * 3;
      const arc   = this.scene.add.arc(x, y, r, 0, 360, false, color, 1).setDepth(5);

      this.scene.tweens.add({
        targets: arc,
        x: x + Math.cos(angle) * speed * 0.6,
        y: y + Math.sin(angle) * speed * 0.6 + (big ? 25 : 12),
        alpha: 0,
        scaleX: 0.1,
        scaleY: 0.1,
        duration: big ? 700 : 450,
        ease: 'Power2',
        onComplete: () => arc.destroy(),
      });
    }
  }

  // ── Level-up Flash ────────────────────────────────────────────────────────

  /** Brief white overlay that fades out — signals a level-up. */
  levelUpFlash(): void {
    const { width, height } = this.scene.scale;
    const flash = this.scene.add
      .rectangle(0, 0, width, height, 0xffffff, 0.6)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(500);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });

    const text = this.scene.add
      .text(width / 2, height * 0.24, t('upgrade.title'), {
        fontSize: '34px',
        fontFamily: 'Arial Black, Arial',
        color: '#ffdd00',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(510)
      .setScale(0.6)
      .setAlpha(0);

    this.scene.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 180,
      ease: 'Back.Out',
      yoyo: true,
      hold: 280,
      onComplete: () => text.destroy(),
    });
  }

  // ── Boss Entrance ─────────────────────────────────────────────────────────

  /** White screen flash + animated "⚠️ BOSS INCOMING" warning text. */
  bossEntranceAnimation(bossName: string): void {
    const { width, height } = this.scene.scale;
    this.screenShake(14, 450);

    // White flash
    const flash = this.scene.add
      .rectangle(0, 0, width, height, 0xffffff, 0.75)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(490);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });

    // Warning text fades in then slides up and fades out
    const banner = this.scene.add
      .rectangle(width / 2, height * 0.4, width, 86, 0x000000, 0.7)
      .setScrollFactor(0)
      .setDepth(491)
      .setAlpha(0);

    const warning = this.scene.add
      .text(width / 2, height * 0.4, `${t('game.boss_incoming')}\n${bossName}`, {
        fontSize: '38px',
        fontFamily: 'Arial Black, Arial',
        color: '#ff1111',
        stroke: '#000000',
        strokeThickness: 8,
        align: 'center',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(491)
      .setAlpha(0);

    this.scene.tweens.add({
      targets: [warning, banner],
      alpha: 1,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [warning, banner],
          alpha: 0,
          y: height * 0.4 - 80,
          duration: 1200,
          delay: 900,
          ease: 'Power2',
          onComplete: () => {
            warning.destroy();
            banner.destroy();
          },
        });
      },
    });
  }

  // ── Wave Announcement ─────────────────────────────────────────────────────

  /** "Wave N" fades in then fades out at the top of the screen. */
  waveAnnouncement(wave: number): void {
    const { width, height } = this.scene.scale;

    const text = this.scene.add
      .text(width / 2, height * 0.22, `${t('game.wave')} ${wave}`, {
        fontSize: '52px',
        fontFamily: 'Arial Black, Arial',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(200)
      .setAlpha(0);

    this.scene.tweens.add({
      targets: text,
      alpha: 1,
      duration: 350,
      ease: 'Power2',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          duration: 700,
          delay: 700,
          ease: 'Power2',
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  // ── Coin Pickup ───────────────────────────────────────────────────────────

  /** Golden circle floats upward and fades — coin pickup feedback. */
  coinPickupAnimation(x: number, y: number, target?: { x: number; y: number }): void {
    const arc = this.scene.add
      .arc(x, y, 6, 0, 360, false, 0xffd700, 1)
      .setDepth(10);

    const destinationX = target?.x ?? x;
    const destinationY = target?.y ?? y - 44;

    this.scene.tweens.add({
      targets: arc,
      x: destinationX,
      y: destinationY,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.In',
      onComplete: () => arc.destroy(),
    });
  }
}
